// Local NYSE market calendar.
//
// The launch build cannot depend on the analytics server for holiday or
// early-close awareness, so compute the exchange calendar locally.

const EARLY_CLOSE_MINUTE = 13 * 60;

function pad(value) {
  return String(value).padStart(2, '0');
}

function dateKey(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function observedFixedHoliday(year, month, day) {
  const holiday = new Date(Date.UTC(year, month - 1, day));
  const weekday = holiday.getUTCDay();

  if (weekday === 6) {
    holiday.setUTCDate(holiday.getUTCDate() - 1);
    return dateKey(holiday.getUTCFullYear(), holiday.getUTCMonth() + 1, holiday.getUTCDate());
  }
  if (weekday === 0) {
    holiday.setUTCDate(holiday.getUTCDate() + 1);
    return dateKey(holiday.getUTCFullYear(), holiday.getUTCMonth() + 1, holiday.getUTCDate());
  }
  return dateKey(year, month, day);
}

function nthWeekdayOfMonth(year, month, weekday, nth) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const firstWeekday = first.getUTCDay();
  const offset = (weekday - firstWeekday + 7) % 7;
  return dateKey(year, month, 1 + offset + (nth - 1) * 7);
}

function lastWeekdayOfMonth(year, month, weekday) {
  const last = new Date(Date.UTC(year, month, 0));
  const lastWeekday = last.getUTCDay();
  const offset = (lastWeekday - weekday + 7) % 7;
  return dateKey(year, month, last.getUTCDate() - offset);
}

function addDays(key, days) {
  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function isWeekend(key) {
  const [year, month, day] = key.split('-').map(Number);
  const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return dateKey(year, month, day);
}

function buildCalendar(year) {
  const holidays = new Set([
    nthWeekdayOfMonth(year, 1, 1, 3),  // Martin Luther King Jr. Day
    nthWeekdayOfMonth(year, 2, 1, 3),  // Presidents Day
    addDays(easterSunday(year), -2),   // Good Friday
    lastWeekdayOfMonth(year, 5, 1),    // Memorial Day
    nthWeekdayOfMonth(year, 9, 1, 1),  // Labor Day
    nthWeekdayOfMonth(year, 11, 4, 4), // Thanksgiving
  ]);

  const fixedHolidays = [
    observedFixedHoliday(year, 1, 1),
    observedFixedHoliday(year, 7, 4),
    observedFixedHoliday(year, 12, 25),
  ];
  for (const key of fixedHolidays) {
    if (key.startsWith(`${year}-`)) {
      holidays.add(key);
    }
  }

  if (year >= 2022) {
    const juneteenth = observedFixedHoliday(year, 6, 19);
    if (juneteenth.startsWith(`${year}-`)) {
      holidays.add(juneteenth);
    }
  }

  const earlyCloses = new Set();

  const dayAfterThanksgiving = addDays(nthWeekdayOfMonth(year, 11, 4, 4), 1);
  if (!holidays.has(dayAfterThanksgiving)) {
    earlyCloses.add(dayAfterThanksgiving);
  }

  const july3 = dateKey(year, 7, 3);
  const july4 = new Date(Date.UTC(year, 6, 4)).getUTCDay();
  if (!holidays.has(july3) && !isWeekend(july3) && july4 >= 1 && july4 <= 5) {
    earlyCloses.add(july3);
  }

  const christmasEve = dateKey(year, 12, 24);
  const christmasDayWeekday = new Date(Date.UTC(year, 11, 25)).getUTCDay();
  if (!holidays.has(christmasEve) && !isWeekend(christmasEve) && christmasDayWeekday >= 1 && christmasDayWeekday <= 5) {
    earlyCloses.add(christmasEve);
  }

  return { holidays, earlyCloses };
}

const calendarByYear = new Map();

function getCalendar(year) {
  if (!calendarByYear.has(year)) {
    calendarByYear.set(year, buildCalendar(year));
  }
  return calendarByYear.get(year);
}

function parseYear(etDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(etDate || '')) return null;
  const year = Number(etDate.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

export function isHoliday(etDate) {
  const year = parseYear(etDate);
  if (!year) return false;
  return getCalendar(year).holidays.has(etDate);
}

export function earlyCloseMinute(etDate) {
  const year = parseYear(etDate);
  if (!year || isWeekend(etDate)) return null;
  return getCalendar(year).earlyCloses.has(etDate) ? EARLY_CLOSE_MINUTE : null;
}

export function __resetMarketCalendarForTests() {
  calendarByYear.clear();
}
